import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeField } from "@/components/shared/TimeField";
import { Sec, SecHint, BreakChips } from "@/components/shared/ShiftSections";
import { Users, Clock, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK, plur } from "@/lib/format";
import { toast } from "sonner";
import { PeoplePicker, useAbsenceMap } from "@/components/shared/PeoplePicker";

// Dva způsoby ručního zápisu: jeden člověk / jedna parta. Oba míří na stejná
// pole, jen hromadný si navíc tahá partu ze zakázky.

const today = () => new Date().toISOString().slice(0, 10);
// Noční směny firma nejezdí, takže odchod dřív než příchod je překlep, ne
// směna přes půlnoc. Nedopočítáváme ho tiše na další den — ukážeme chybu,
// jinak by z překlepu vznikl dvacetihodinový záznam ve mzdách.
const hoursBetween = (d, a, b, brk) => {
  if (!d || !a || !b || b <= a) return 0;
  const ms = new Date(`${d}T${b}`) - new Date(`${d}T${a}`);
  return Math.max(ms / 3600000 - (Number(brk) || 0) / 60, 0);
};
const badTime = (m) => !!m.t_in && !!m.t_out && m.t_out <= m.t_in;
const fmtH = (h) => `${h.toFixed(1).replace(".", ",")} h`;

// Čas a přestávka ve stejném vzhledu jako dialog „Odhlásit se z práce“ (ShiftSections).
function TimeRow({ m, setM, prefix }) {
  return (
    <>
      <Sec icon={Clock} title="Čas" testid={`${prefix}-sec-time`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="min-w-0"><Label>Datum</Label>
            <Input type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid={`${prefix}-date`} /></div>
          <div className="min-w-0"><Label>Příchod</Label>
            <div className="mt-1"><TimeField value={m.t_in} onChange={(v) => setM({ ...m, t_in: v })} testid={`${prefix}-tin`} label="Příchod" /></div></div>
          <div className="min-w-0"><Label>Odchod</Label>
            <div className="mt-1"><TimeField value={m.t_out} onChange={(v) => setM({ ...m, t_out: v })} testid={`${prefix}-tout`} label="Odchod" /></div></div>
        </div>
      </Sec>
      <div data-testid={`${prefix}-sec-break`}>
        <BreakChips value={m.break_minutes} onChange={(v) => setM({ ...m, break_minutes: v })} withAuto testid={`${prefix}-break`}
          autoMinutes={(() => { const t = (x) => { const [h, mm] = (x || "0:0").split(":").map(Number); return h * 60 + mm; }; let s = t(m.t_out) - t(m.t_in); if (s < 0) s += 1440; return s > 360 ? 30 : 0; })()} />
      </div>
    </>
  );
}

export function AttendanceSingleDialog({ open, onClose, workers, projects, onSaved, initial, topSlot }) {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setM({ user_id: initial?.user_id || "", project_id: "", date: initial?.date || today(), t_in: "07:00", t_out: "15:30", break_minutes: "30", note: "" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !m) return null;

  const worker = workers.find((w) => w.id === m.user_id);
  const h = hoursBetween(m.date, m.t_in, m.t_out, m.break_minutes);

  const submit = async () => {
    if (!m.user_id || !m.project_id) return toast.error("Vyberte pracovníka a zakázku.");
    if (badTime(m)) return toast.error("Odchod musí být po příchodu.");
    setBusy(true);
    try {
      await api.post("/attendance/manual", {
        user_id: m.user_id, project_id: m.project_id,
        check_in: `${m.date}T${m.t_in}`, check_out: `${m.date}T${m.t_out}`,
        break_minutes: m.break_minutes === "" ? null : Number(m.break_minutes), note: m.note || null });
      toast.success("Záznam přidán."); onClose(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent sheet onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[620px] md:max-h-[90vh] overflow-y-auto max-md:pt-8" data-testid="single-dialog">
        <DialogHeader>
          <DialogTitle>Jednotlivý záznam</DialogTitle>
          <p className="text-muted-foreground">Jeden člověk, jedna směna</p>
        </DialogHeader>
        {topSlot}
        <div className="space-y-4">
          <Sec icon={Users} title="Kdo a kde" testid="single-sec-who">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0"><Label>Pracovník <span className="text-destructive">*</span></Label>
              <Select value={m.user_id || undefined} onValueChange={(v) => setM({ ...m, user_id: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="single-worker"><SelectValue placeholder="Vyberte pracovníka" /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="min-w-0"><Label>Zakázka <span className="text-destructive">*</span></Label>
              <Select value={m.project_id || undefined} onValueChange={(v) => setM({ ...m, project_id: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="single-project"><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          </Sec>
          <TimeRow m={m} setM={setM} prefix="single" />
          <Sec icon={Pencil} title={<>Na čem se dělalo <SecHint>volitelné</SecHint></>} testid="single-sec-report">
            <Textarea rows={3} value={m.note} onChange={(e) => setM({ ...m, note: e.target.value })} placeholder="Např. betonáž základů, montáž bednění…" className="w-full min-w-0" data-testid="single-note" />
          </Sec>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-border pt-3">
          <div className="text-sm text-muted-foreground" data-testid="single-summary">
            {badTime(m)
              ? <span className="text-destructive font-semibold">Odchod musí být po příchodu.</span>
              : <>{fmtH(h)}{worker?.hourly_rate ? <> · <b className="text-foreground">{formatCZK(Math.round(h * worker.hourly_rate))}</b></> : null} · ruční záznam bez GPS</>}
          </div>
          <div className="flex flex-col-reverse md:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="h-12 md:h-11 w-full md:w-auto">Zrušit</Button>
            <Button onClick={submit} disabled={busy || badTime(m)} className="h-12 md:h-11 w-full md:w-auto font-bold" data-testid="single-save">Přidat záznam</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AttendanceBulkDialog({ open, onClose, projects, onSaved }) {
  const [m, setM] = useState(null);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setM({ project_id: "", date: today(), t_in: "07:00", t_out: "15:30", break_minutes: "30", note: "", sel: [] }); setCrew([]); }
  }, [open]);

  // Parta se načte až po výběru zakázky — nabídnou se JEN lidé na ni přihlášení.
  const pid = m?.project_id;
  useEffect(() => {
    if (!pid) { setCrew([]); return; }
    setLoading(true);
    api.get(`/projects/${pid}/crew`)
      .then((r) => setCrew(r.data.crew || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [pid]);

  const absences = useAbsenceMap(m?.date, m?.date, !!open);
  if (!open || !m) return null;
  const h = hoursBetween(m.date, m.t_in, m.t_out, m.break_minutes);
  const picked = crew.filter((c) => m.sel.includes(c.id));
  const cost = picked.reduce((s, c) => s + Math.round(h * (c.hourly_rate || 0)), 0);
  const toggle = (id) => setM({ ...m, sel: m.sel.includes(id) ? m.sel.filter((x) => x !== id) : [...m.sel, id] });
  const allOn = crew.length > 0 && picked.length === crew.length;

  const submit = async () => {
    if (!m.project_id) return toast.error("Vyberte zakázku.");
    if (!m.sel.length) return toast.error("Vyberte aspoň jednoho člověka.");
    if (badTime(m)) return toast.error("Odchod musí být po příchodu.");
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/manual/bulk", {
        project_id: m.project_id, user_ids: m.sel,
        check_in: `${m.date}T${m.t_in}`, check_out: `${m.date}T${m.t_out}`,
        break_minutes: m.break_minutes === "" ? null : Number(m.break_minutes), note: m.note || null });
      toast.success(`Přidáno ${plur(data.created, "záznam", "záznamy", "záznamů")}.`); onClose(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent sheet onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[620px] md:max-h-[90vh] overflow-y-auto max-md:pt-8" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle>Hromadný záznam</DialogTitle>
          <p className="text-muted-foreground">Jedna zakázka, jeden čas, více lidí</p>
        </DialogHeader>
        <div className="space-y-4">
          <Sec icon={Users} title="Kde a kdo" testid="bulk-sec-who">
          <div><Label>Zakázka <span className="text-destructive">*</span></Label>
            <Select value={m.project_id || undefined} onValueChange={(v) => setM({ ...m, project_id: v, sel: [] })}>
              <SelectTrigger className="h-11 mt-1" data-testid="bulk-project"><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <Label>Lidé na zakázce{crew.length ? ` — ${crew.length}` : ""}</Label>
              {crew.length > 0 && (
                <button type="button" onClick={() => setM({ ...m, sel: allOn ? [] : crew.map((c) => c.id) })}
                  className="text-sm font-semibold text-brand hover:underline" data-testid="bulk-toggle-all">
                  {allOn ? "Odebrat všechny" : "Vybrat všechny"}
                </button>
              )}
            </div>
            {(!m.project_id || loading || !crew.length) ? (
              <div className="border border-border bg-card rounded-[14px] p-4 text-sm text-muted-foreground" data-testid="bulk-crew">
                {!m.project_id ? "Nejprve vyberte zakázku." : loading ? "Načítám partu…" : "Na tuto zakázku není nikdo přihlášený. Přiřaďte lidi v detailu zakázky."}
              </div>
            ) : (
              // 179: hledání + kdo má ten den volno / lékaře / nemoc; bez hodinovek.
              <PeoplePicker people={crew} selected={m.sel} onToggle={toggle} absences={absences} testid="bulk-crew"
                rowTestid={(c) => `bulk-crew-${c.id}`} sub={(c) => c.position || null} maxH="max-h-72" />
            )}
            <p className="text-xs text-muted-foreground mt-2">Nabízí se jen lidé přihlášení na vybranou zakázku.</p>
          </div>
          </Sec>
          <TimeRow m={m} setM={setM} prefix="bulk" />
          <Sec icon={Pencil} title={<>Na čem se dělalo <SecHint>volitelné</SecHint></>} testid="bulk-sec-report">
            <Textarea rows={2} value={m.note} onChange={(e) => setM({ ...m, note: e.target.value })} placeholder="Společný report pro všechny…" className="w-full min-w-0" data-testid="bulk-note" />
          </Sec>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-border pt-3">
          <div className="text-sm text-muted-foreground" data-testid="bulk-summary">
            {badTime(m)
              ? <span className="text-destructive font-semibold">Odchod musí být po příchodu.</span>
              : picked.length
              ? <>{plur(picked.length, "člověk", "lidé", "lidí")} × {fmtH(h)} = <b className="text-foreground">{formatCZK(cost)}</b></>
              : "Vyberte aspoň jednoho člověka"}
          </div>
          <div className="flex flex-col-reverse md:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="h-12 md:h-11 w-full md:w-auto">Zrušit</Button>
            <Button onClick={submit} disabled={busy || !picked.length || badTime(m)} className="h-12 md:h-11 w-full md:w-auto font-bold" data-testid="bulk-save">
              {picked.length ? `Přidat ${plur(picked.length, "záznam", "záznamy", "záznamů")}` : "Přidat záznamy"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
